import { IS_STAGING_BUILD } from "./staging-flag";
import { isAnalyticsMutedForThisLoad } from "./analytics/embed-mute";

type SentryUser = {
  id: string;
  email?: string;
  username?: string;
};

const PRODUCTION_HOSTS = new Set(["yumesaku.tech", "chotdekiru.com"]);
const MAX_QUEUED_ERRORS = 20;

let sentryModulePromise: Promise<
  typeof import("@sentry/react-router")
> | null = null;
let sentryInitialized = false;
let initializationScheduled = false;
let listeningForEarlyErrors = false;
let pendingUser: SentryUser | null | undefined;
type SentryTags = Record<string, string>;
type QueuedError = { error: unknown; tags?: SentryTags };

const pendingErrors: QueuedError[] = [];

function isProductionBrowser(): boolean {
  return (
    typeof window !== "undefined" &&
    PRODUCTION_HOSTS.has(window.location.hostname) &&
    // 親のページが計測している埋め込み (/skillmap/demo?embed=1) では動かさない
    !isAnalyticsMutedForThisLoad()
  );
}

function queueError(error: unknown, tags?: SentryTags): void {
  if (error == null || pendingErrors.some((queued) => queued.error === error)) {
    return;
  }
  if (pendingErrors.length === MAX_QUEUED_ERRORS) pendingErrors.shift();
  pendingErrors.push({ error, tags });
}

function handleWindowError(event: ErrorEvent): void {
  queueError(event.error ?? new Error(event.message));
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  queueError(event.reason);
}

function listenForEarlyErrors(): void {
  if (listeningForEarlyErrors || !isProductionBrowser()) return;
  listeningForEarlyErrors = true;
  window.addEventListener("error", handleWindowError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
}

function stopListeningForEarlyErrors(): void {
  if (!listeningForEarlyErrors || typeof window === "undefined") return;
  listeningForEarlyErrors = false;
  window.removeEventListener("error", handleWindowError);
  window.removeEventListener("unhandledrejection", handleUnhandledRejection);
}

async function initializeSentry() {
  if (IS_STAGING_BUILD || !isProductionBrowser()) return null;

  if (!sentryModulePromise) {
    sentryModulePromise = import("@sentry/react-router").catch((error) => {
      sentryModulePromise = null;
      throw error;
    });
  }

  const Sentry = await sentryModulePromise;
  if (sentryInitialized) return Sentry;

  Sentry.init({
    dsn: "https://fd8b9e0b54cd62e1af5237ffead87929@o4508291417702400.ingest.us.sentry.io/4511478006349824",
    environment: "production",
    enabled: true,
    tracesSampleRate: 0.05,
    integrations: [Sentry.reactRouterTracingIntegration()],
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      /*
      通信の取り消し。

      読み込みの途中で次のページへ進んだ、タブを閉じた、戻るを押した。
      どれも人の操作そのもので、直すところが無い。文言は browser ごとに
      違う (WebKit は "Fetch is aborted"、Chrome は "The user aborted a
      request.") ので、名前で拾う。

      **これを消してよいのは、client のどこにも時間切れの abort が無いから。**
      AbortSignal.timeout も、setTimeout からの controller.abort() も使って
      いない。時間切れで切る箇所を足すときは、ここも一緒に考え直すこと。
      入れたまま足すと、API が遅いという知らせがここで捨てられる。
      */
      /AbortError/,
    ],
  });
  sentryInitialized = true;

  if (pendingUser !== undefined) Sentry.setUser(pendingUser);
  for (const { error, tags } of pendingErrors.splice(0)) {
    Sentry.captureException(error, tags ? { tags } : undefined);
  }
  stopListeningForEarlyErrors();

  return Sentry;
}

function initializeWithoutBlocking(): void {
  void initializeSentry().catch(() => {
    // A monitoring failure must never break the application. A later route
    // error can retry because a failed dynamic import clears the promise.
  });
}

function initializeWhenIdle(): void {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(initializeWithoutBlocking, { timeout: 5_000 });
    return;
  }
  window.setTimeout(initializeWithoutBlocking, 3_000);
}

/**
 * Keep Sentry out of the hydration and load critical path. Lightweight global
 * listeners retain a bounded set of errors until the monitoring chunk loads.
 */
export function scheduleSentryInitialization(): void {
  if (initializationScheduled || !isProductionBrowser()) return;
  initializationScheduled = true;
  listenForEarlyErrors();

  if (document.readyState === "complete") {
    initializeWhenIdle();
  } else {
    window.addEventListener("load", initializeWhenIdle, { once: true });
  }
}

export function setSentryUser(user: SentryUser | null): void {
  pendingUser = user;
  if (!sentryInitialized) return;

  void initializeSentry()
    .then((Sentry) => Sentry?.setUser(user))
    .catch(() => undefined);
}

export function captureSentryException(
  error: unknown,
  tags?: SentryTags,
): void {
  if (!isProductionBrowser()) return;
  if (!sentryInitialized) {
    queueError(error, tags);
    initializeWithoutBlocking();
    return;
  }

  void initializeSentry()
    .then((Sentry) =>
      Sentry?.captureException(error, tags ? { tags } : undefined),
    )
    .catch(() => undefined);
}
